import { createContext, useContext } from "react";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/** Who moved the reader to a field. */
export enum ProposalFieldTraversalSource {
  /** A deliberate "Prev / Next Change" press, and only this opens the destination field's change history unasked. */
  Traverser = "traverser",
  /**
   * A field reporting its own traverse-in — a focus, or a click on its Edit button — which is the reader
   * reaching for the field itself, not for its history.
   */
  Field = "field",
}

/**
 * A standing request from the proposal page for one field to open its own change history.
 *
 * "Prev / Next Change" stops at every field carrying an unread counterparty change. What the reader
 * needs at such a stop is that field's change history — not the proposal's one conversation, which is
 * what the stop used to surface instead. The traverser sits in the page header/footer while the
 * history control sits inside whichever tab renders the field, so the page publishes the field here
 * and the control that owns that field honors it. Passing it by context rather than threading a prop
 * keeps every tab in between a transparent pass-through, as `ProposalCarriedOverNegotiationContext`
 * does for the negotiation a revision continues.
 *
 * Undefined when no page provides it (the standalone history popover, tests), so a control then only
 * ever opens from its own click.
 */
export type ProposalFieldHistoryRequest = {
  /** The field asked to open, or undefined when nothing is being asked for. */
  field?: ProposalField;
  /** Called by the control that honored the request once it closes, so the request cannot reopen it. */
  onHonored: () => void;
};

export const ProposalFieldHistoryRequestContext = createContext<ProposalFieldHistoryRequest | undefined>(
  undefined,
);

/** Whether `field` is the field the page asked to open, with the acknowledgement to call on close. */
export function useProposalFieldHistoryRequest(field: ProposalField | undefined) {
  const request = useContext(ProposalFieldHistoryRequestContext);
  const isRequested = Boolean(field && request?.field?.isEqualTo(field));
  return { isRequested, onHonored: request?.onHonored };
}
